/**
 * MeetingArchiveSection — 会议纪要归档入口。
 *
 * 归档产出的是一份纪要文档：进文档库、可被全文与语义检索找到、可以在对话里作为
 * 资料勾选。入口要把这三件事讲清楚——归档产出了什么、去了哪里、之后在哪能找到。
 *
 * 未绑定事项的会议照常可以归档，纪要先归入归档人的个人资料；会议之后绑定事项时，
 * 纪要会一并移入事项资料。
 */

import { Alert, Button, Space, Tooltip, Typography } from "antd";
import { FileTextOutlined, InboxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { Meeting } from "../../types/meeting";

const { Paragraph, Text } = Typography;

function formatMoment(value: string | null): string {
  if (!value) return "";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

interface MeetingArchiveSectionProps {
  meeting: Meeting;
  archiving: boolean;
  /** 上一次归档失败的原因；为 null 时不显示。 */
  errorMessage: string | null;
  onArchive: () => void;
  /**
   * 纪要是否已经过本人确认（S11 确认门禁）。为 false 时归档按钮禁用并提示
   * 「请先确认纪要」——未确认的纪要是 AI 草稿，不允许进卷宗。默认 true，
   * 兼容尚未接入确认门禁的调用方。
   */
  confirmed?: boolean;
}

export default function MeetingArchiveSection({
  meeting,
  archiving,
  errorMessage,
  onArchive,
  confirmed = true,
}: MeetingArchiveSectionProps) {
  const navigate = useNavigate();
  const archived = !!meeting.minutesDocumentId;
  const summaryReady = meeting.summaryState === "ready";
  const boundToMatter = !!meeting.matterId;
  // 先查总结是否就绪，再查是否已确认：未生成总结时不可能被确认（确认门禁
  // 依赖总结先就绪），此时该给「先生成总结」而不是「先确认」——顺序反了会让
  // 律师去点一个此刻根本点不出结果的确认按钮。
  const blockedReason = !summaryReady
    ? "纪要以会后总结为主体，先生成会后总结再归档。"
    : !confirmed
      ? "请先确认纪要"
      : null;

  const destination = boundToMatter
    ? "纪要会存入本会议所属事项的资料里，事项成员都能查到。"
    : "这场会还没有关联事项，纪要先归入你的个人资料；之后把会议关联到事项时，纪要会一并移入事项资料。";

  if (archived) {
    return (
      <div data-testid="meeting-archive-done">
        <Alert
          type="success"
          showIcon
          icon={<FileTextOutlined />}
          message="纪要已归档"
          description={
            <span>
              已生成一份会议纪要文档，存放在
              {boundToMatter ? "本会议所属事项的资料" : "你的个人资料"}
              里，可以在文档库中按标题或内容检索到，也可以在对话里作为资料勾选。
              {meeting.archivedAt && (
                <>
                  <br />
                  <Text type="secondary">归档时间 {formatMoment(meeting.archivedAt)}</Text>
                </>
              )}
            </span>
          }
        />
        <Space style={{ marginTop: "var(--jerry-sp-3)" }} wrap>
          <Button
            icon={<FileTextOutlined />}
            data-testid="meeting-archive-open-document"
            onClick={() =>
              navigate(
                boundToMatter ? `/matters/${meeting.matterId}?tab=docs` : "/personal-space",
              )
            }
          >
            查看纪要文档
          </Button>
          <Button
            loading={archiving}
            disabled={!summaryReady}
            onClick={onArchive}
            data-testid="meeting-archive-refresh"
          >
            用最新摘要更新纪要
          </Button>
          <Text type="secondary">重新归档更新同一份纪要文档，不会多出第二份。</Text>
        </Space>
      </div>
    );
  }

  return (
    <div data-testid="meeting-archive-entry">
      <Paragraph type="secondary" style={{ marginBottom: "var(--jerry-sp-2)" }}>
        归档会把会议信息、摘要、关键讨论点、决议、行动项和纪要全文组装成一份会议纪要文档
        存入文档库，之后可以按标题或内容检索到，也可以在对话里作为资料勾选。
        {destination}
      </Paragraph>
      {errorMessage && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          data-testid="meeting-archive-error"
          message="归档未完成"
          description={errorMessage}
        />
      )}
      <Space wrap>
        {blockedReason ? (
          <Tooltip title={blockedReason}>
            <Button
              type="primary"
              icon={<InboxOutlined />}
              disabled
              data-testid="meeting-archive-submit"
            >
              归档会议纪要
            </Button>
          </Tooltip>
        ) : (
          <Button
            type="primary"
            icon={<InboxOutlined />}
            loading={archiving}
            onClick={onArchive}
            data-testid="meeting-archive-submit"
          >
            归档会议纪要
          </Button>
        )}
        {blockedReason && (
          <Text type="secondary" data-testid="meeting-archive-blocked">
            {blockedReason}
          </Text>
        )}
      </Space>
    </div>
  );
}
